<script setup>
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Draggable from 'vuedraggable';

import ConversationApi from 'dashboard/api/inbox/conversation';
import KanbansAPI from 'dashboard/api/kanbans';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KanbanCard from './KanbanCard.vue';
import KanbanContactLightbox from 'dashboard/components-next/Kanban/ContactLightbox/KanbanContactLightbox.vue';
import KanbanCreateCardDialog from './components/KanbanCreateCardDialog.vue';

const props = defineProps({
  kanbanId: {
    type: Number,
    required: true,
  },
  // Conversation to scroll to + highlight once the board loads — see routes.js
  // (?conversation_id=) and conversation/kanban/KanbanBox.vue's "Abrir Kanban" button.
  conversationId: {
    type: Number,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();
const { accountScopedUrl } = useAccount();

const isFetchingConversations = ref(false);
/** @type {import('vue').Ref<Record<number, Array<object>>>} */
const conversationsByStage = ref({});
/** @type {import('vue').Ref<Record<number, Array<object>>>} */
const notesByConversationId = ref({});

const kanban = useMapGetter('kanbans/getKanban');
const stages = useMapGetter('kanbans/getStagesByKanbanId');
const kanbanUiFlags = useMapGetter('kanbans/getUIFlags');

const currentKanban = computed(() => kanban.value(props.kanbanId));
const currentStages = computed(() => stages.value(props.kanbanId));

// Tints each column's background with a faint wash of its own stage color,
// echoing the reference board without hardcoding a fixed palette per stage.
const stageTintStyle = color => {
  if (!color || typeof color !== 'string' || !color.startsWith('#')) return {};
  return { backgroundColor: `${color}0d` };
};

const scrollToHighlightedConversation = async () => {
  if (!props.conversationId) return;
  await nextTick();
  const card = document.querySelector(
    `[data-conversation-id="${props.conversationId}"]`
  );
  card?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'center',
  });
};

// The conversations index endpoint is paginated (CONVERSATION_RESULTS_PER_PAGE,
// 25 by default) and kanban_id only narrows the WHERE clause — it doesn't
// paginate per stage. A single page can't be assumed to hold every conversation
// across every column of the board, so we page through until `all_count` (total
// matching the kanban filter, returned in meta) is fully accounted for.
const fetchConversations = async () => {
  isFetchingConversations.value = true;
  try {
    const allConversations = [];
    let page = 1;
    let totalCount = Infinity;
    while (allConversations.length < totalCount) {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await ConversationApi.get({
        kanbanId: props.kanbanId,
        status: 'all',
        page,
      });
      const payload = data.data.payload || [];
      totalCount = data.data.meta?.all_count ?? payload.length;
      if (!payload.length) break;
      allConversations.push(...payload);
      page += 1;
    }

    const grouped = {};
    currentStages.value.forEach(stage => {
      grouped[stage.id] = [];
    });
    allConversations.forEach(conversation => {
      const stageId = conversation.kanban_stage_id;
      if (!grouped[stageId]) grouped[stageId] = [];
      grouped[stageId].push(conversation);
    });
    Object.values(grouped).forEach(list =>
      list.sort((a, b) => (a.kanban_position || 0) - (b.kanban_position || 0))
    );
    conversationsByStage.value = grouped;
    fetchPrivateNotes(allConversations.map(c => c.id));
    scrollToHighlightedConversation();
  } finally {
    isFetchingConversations.value = false;
  }
};

// One request for every card currently on the board instead of one per card —
// see Kanbans::CardsController#private_notes on the backend.
const fetchPrivateNotes = async conversationIds => {
  if (!conversationIds.length) {
    notesByConversationId.value = {};
    return;
  }
  try {
    const { data } = await KanbansAPI.getPrivateNotes({
      kanbanId: props.kanbanId,
      conversationIds,
    });
    notesByConversationId.value = data.notes_by_conversation_id || {};
  } catch (error) {
    notesByConversationId.value = {};
  }
};

const openConversation = conversation => {
  window.open(accountScopedUrl(`conversations/${conversation.id}`), '_blank');
};

const lightboxRef = ref(null);
const openLightbox = conversation => {
  const notes = notesByConversationId.value[conversation.id] || [];
  lightboxRef.value?.open(conversation, notes);
};

const handleNoteAdded = ({ conversationId: id, note }) => {
  const current = notesByConversationId.value[id] || [];
  notesByConversationId.value = {
    ...notesByConversationId.value,
    [id]: [note, ...current].slice(0, 3),
  };
};

const createCardDialogRef = ref(null);
const openCreateCardDialog = (stageId = null) => {
  createCardDialogRef.value?.open(stageId);
};
const onCardCreated = () => {
  fetchConversations();
};

const resolveDialogRef = ref(null);
const pendingResolveConversation = ref(null);
const isResolving = ref(false);

const openResolveDialog = conversation => {
  pendingResolveConversation.value = conversation;
  resolveDialogRef.value?.open();
};

const removeCardFromBoard = conversation => {
  const stageId = conversation.kanban_stage_id;
  const list = conversationsByStage.value[stageId];
  if (!list) return;
  conversationsByStage.value[stageId] = list.filter(
    c => c.id !== conversation.id
  );
};

const handleResolveConfirm = async () => {
  const conversation = pendingResolveConversation.value;
  if (!conversation) return;

  isResolving.value = true;
  try {
    await Promise.all([
      ConversationApi.toggleStatus({
        conversationId: conversation.id,
        status: 'resolved',
      }),
      ConversationApi.moveToKanbanStage({
        conversationId: conversation.id,
        kanbanStageId: null,
        kanbanPosition: 0,
      }),
    ]);
    removeCardFromBoard(conversation);
    useAlert(t('KANBAN.RESOLVE_CARD.SUCCESS'));
    resolveDialogRef.value?.close();
  } catch (error) {
    useAlert(t('KANBAN.RESOLVE_CARD.ERROR'));
  } finally {
    isResolving.value = false;
  }
};

const persistMove = async (conversation, stageId, newIndex) => {
  try {
    await ConversationApi.moveToKanbanStage({
      conversationId: conversation.id,
      kanbanStageId: stageId,
      kanbanPosition: newIndex,
    });
  } catch (error) {
    // Revert on failure by refetching the board state
    fetchConversations();
  }
};

const handleColumnChange = (stage, event) => {
  if (event.added) {
    persistMove(event.added.element, stage.id, event.added.newIndex);
  } else if (event.moved) {
    persistMove(event.moved.element, stage.id, event.moved.newIndex);
  }
};

onMounted(async () => {
  if (!Object.keys(store.state.kanbans.records).length) {
    await store.dispatch('kanbans/get');
  }
  fetchConversations();
});

watch(() => props.kanbanId, fetchConversations);
// Handles navigating from one conversation's "Abrir Kanban" to another while already on the
// same board (fetchConversations wouldn't re-run since kanbanId is unchanged).
watch(() => props.conversationId, scrollToHighlightedConversation);
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <header
      class="flex items-center justify-between h-16 px-6 border-b border-n-weak shrink-0"
    >
      <div class="flex items-center gap-3">
        <Icon icon="i-lucide-kanban-square" class="text-n-ruby-9 size-5" />
        <h1 class="text-lg font-bold text-n-slate-12">
          {{ currentKanban?.name || t('SIDEBAR.KANBAN') }}
        </h1>
      </div>
      <div class="flex items-center gap-2">
        <NextButton
          xs
          ruby
          icon="i-lucide-plus"
          :label="t('KANBAN.CREATE_CARD.HEADER_BUTTON')"
          @click="openCreateCardDialog()"
        />
        <router-link :to="accountScopedUrl('settings/kanban')">
          <NextButton
            faded
            xs
            slate
            icon="i-lucide-settings"
            :label="t('SIDEBAR.SETTINGS')"
          />
        </router-link>
      </div>
    </header>

    <div
      v-if="kanbanUiFlags.isFetching || isFetchingConversations"
      class="flex items-center justify-center flex-1"
    >
      <Spinner class="size-6" />
    </div>

    <div v-else class="flex-1 px-6 pt-6 pb-2 overflow-x-auto overflow-y-hidden">
      <div class="flex h-full gap-6 pb-4 min-w-max">
        <div
          v-for="stage in currentStages"
          :key="stage.id"
          class="w-[320px] shrink-0 flex flex-col h-full rounded-xl"
          :style="stageTintStyle(stage.color)"
        >
          <div class="flex items-center justify-between px-2 py-3">
            <div class="flex items-center gap-2 min-w-0">
              <span
                class="rounded-full size-2.5 shrink-0"
                :style="{ backgroundColor: stage.color }"
              />
              <h2 class="text-sm font-bold truncate text-n-slate-12">
                {{ stage.name }}
              </h2>
              <span
                class="px-2 py-0.5 text-xs font-semibold rounded-full bg-n-solid-1 text-n-slate-11 border border-n-weak shrink-0"
              >
                {{ (conversationsByStage[stage.id] || []).length }}
              </span>
            </div>
            <button
              type="button"
              :title="t('KANBAN.CREATE_CARD.COLUMN_BUTTON_ARIA')"
              class="p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors shrink-0"
              @click="openCreateCardDialog(stage.id)"
            >
              <Icon icon="i-lucide-plus" class="size-4" />
            </button>
          </div>

          <Draggable
            :list="conversationsByStage[stage.id] || []"
            :group="{ name: 'kanban-board' }"
            item-key="id"
            class="flex-1 px-2 pb-10 space-y-3 overflow-y-auto kanban-scroll"
            ghost-class="kanban-card-ghost"
            @change="handleColumnChange(stage, $event)"
          >
            <template #item="{ element: conversation }">
              <KanbanCard
                :conversation="conversation"
                :is-highlighted="conversation.id === conversationId"
                :private-notes="notesByConversationId[conversation.id] || []"
                @open-lightbox="openLightbox"
                @open-conversation="openConversation"
                @resolve-and-remove="openResolveDialog"
              />
            </template>
          </Draggable>

          <p
            v-if="!(conversationsByStage[stage.id] || []).length"
            class="px-2 pb-6 text-xs text-center text-n-slate-9"
          >
            {{ t('KANBAN.EMPTY_STAGE') }}
          </p>
        </div>

        <div
          v-if="!currentStages.length"
          class="flex flex-col items-center justify-center flex-1 text-n-slate-11"
        >
          <Icon icon="i-lucide-kanban-square" class="mb-2 size-8" />
          <p class="text-sm">{{ t('KANBAN.NO_STAGES') }}</p>
          <router-link :to="accountScopedUrl('settings/kanban')">
            <NextButton
              faded
              sm
              icon="i-lucide-plus"
              class="mt-3"
              :label="t('SIDEBAR.NEW_KANBAN_STAGE')"
            />
          </router-link>
        </div>
      </div>
    </div>

    <KanbanContactLightbox
      ref="lightboxRef"
      @note-added="handleNoteAdded"
    />
    <KanbanCreateCardDialog
      ref="createCardDialogRef"
      :kanban-id="kanbanId"
      @created="onCardCreated"
    />
    <Dialog
      ref="resolveDialogRef"
      type="alert"
      :title="t('KANBAN.RESOLVE_CARD.TITLE')"
      :description="t('KANBAN.RESOLVE_CARD.DESCRIPTION')"
      :confirm-button-label="t('KANBAN.RESOLVE_CARD.CONFIRM')"
      :is-loading="isResolving"
      @confirm="handleResolveConfirm"
    />
  </div>
</template>

<style scoped lang="scss">
.kanban-card-ghost {
  @apply opacity-50 bg-n-slate-3 dark:bg-n-solid-2;
}

.kanban-scroll::-webkit-scrollbar {
  width: 6px;
}
.kanban-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.kanban-scroll::-webkit-scrollbar-thumb {
  background-color: rgb(var(--slate-7));
  border-radius: 10px;
}
</style>
